import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RotateCw } from 'lucide-react';
import { useDemoHint } from './DemoHint';
import { AuthCard } from './AuthCard';

/*
 * The sign-in card as a *selected text box* on the demo page — and the one object visitors can really handle:
 *  - the eight handles resize it (west / north handles move that edge, like the editor; the form reflows inside;
 *    the box never gets narrower than the form needs or shorter than its content),
 *  - the round grip rotates it about its centre (Shift = 15° steps, snaps to 0 / 90 / 180 / 270 within 4°, live angle
 *    readout) — the form keeps working at any angle.
 * Geometry is in page px (`obj` = { x, y, w, minH, rot }); the owner keeps the state so the status bar can show it.
 * `flow` (compact phones) drops the absolute geometry: the box fills its column, rotation still works, resize is inert.
 */

const SELECTION = 'hsl(var(--wp-selection))';
const MIN_W = 280;
const HANDLES = ['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'];
const SIGNS = { nw: [-1, -1], n: [0, -1], ne: [1, -1], w: [-1, 0], e: [1, 0], sw: [-1, 1], s: [0, 1], se: [1, 1] };
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const round = (v) => Math.round(v * 2) / 2;

const HANDLE_AT = (pos) => {
  const off = -10; // the 20 px hit area is centred on the 9 px handle
  return { nw: { top: off, left: off }, n: { top: off, left: '50%', marginLeft: off }, ne: { top: off, right: off }, w: { top: '50%', left: off, marginTop: off }, e: { top: '50%', right: off, marginTop: off }, sw: { bottom: off, left: off }, s: { bottom: off, left: '50%', marginLeft: off }, se: { bottom: off, right: off } }[pos];
};

export const DemoAuthObject = ({ obj, onChange, bounds, flow = false, zoom = 1, mode, onModeChange, initialError, notice, footer, onMeasure }) => {
  const { bind, pulse, dismiss, suspend } = useDemoHint();
  const wrapRef = useRef(null);
  const frameRef = useRef(null);
  const contentRef = useRef(null);
  const objRef = useRef(obj);
  objRef.current = obj;
  const zoomRef = useRef(zoom); // screen px -> page px while the page is scaled
  zoomRef.current = zoom || 1;
  const drag = useRef(null);
  const [interacting, setInteracting] = useState(null); // 'resize' | 'rotate' | null

  // the rendered height feeds the status bar readout and the reset pill's position
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const report = () => onMeasure?.(el.offsetHeight);
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onMeasure]);

  const begin = (kind, e) => {
    e.preventDefault();
    e.stopPropagation();
    dismiss();
    suspend(true);
    document.body.classList.add('wp-demo-dragging'); // no text selection on the page while dragging
    window.getSelection?.()?.removeAllRanges?.();
    setInteracting(kind);
  };

  const startResize = (e, pos) => {
    if (flow || e.button > 0) return;
    begin('resize', e);
    const o = objRef.current;
    drag.current = { kind: 'resize', pos, px: e.clientX, py: e.clientY, x: o.x, y: o.y, w: o.w, h: frameRef.current.offsetHeight, contentH: contentRef.current.offsetHeight, rot: o.rot, minH: o.minH };
    document.body.style.cursor = `${pos}-resize`;
  };

  const startRotate = (e) => {
    if (e.button > 0) return;
    begin('rotate', e);
    const r = wrapRef.current.getBoundingClientRect();
    const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    drag.current = { kind: 'rotate', cx, cy, start: Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI), rot0: objRef.current.rot };
    document.body.style.cursor = 'grabbing';
  };

  const onMove = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    const o = objRef.current;
    if (d.kind === 'rotate') {
      const angle = Math.atan2(e.clientY - d.cy, e.clientX - d.cx) * (180 / Math.PI);
      let rot = d.rot0 + (angle - d.start);
      rot = ((rot % 360) + 360) % 360;
      if (e.shiftKey) rot = Math.round(rot / 15) * 15;
      for (const s of [0, 90, 180, 270, 360]) if (Math.abs(rot - s) < 4) rot = s % 360;
      onChange({ ...o, rot: Math.round(rot * 10) / 10 });
      return;
    }
    // resize: pointer delta expressed in the box's own (rotated) axes, then the moved edge shifts the centre. The
    // opposite edge stays anchored; the box may grow to the page edge (not just the margin), like the editor.
    const dx = (e.clientX - d.px) / zoomRef.current, dy = (e.clientY - d.py) / zoomRef.current;
    const rad = (d.rot * Math.PI) / 180, cos = Math.cos(rad), sin = Math.sin(rad);
    const ldx = dx * cos + dy * sin, ldy = -dx * sin + dy * cos;
    const [sx, sy] = SIGNS[d.pos];
    const maxW = sx > 0 ? bounds.w - d.x : d.x + d.w;
    const maxH = sy > 0 ? bounds.h - d.y : d.y + d.h;
    const newW = sx ? clamp(d.w + sx * ldx, MIN_W, Math.max(MIN_W, maxW)) : d.w;
    const newH = sy ? clamp(d.h + sy * ldy, d.contentH, Math.max(d.contentH, maxH)) : d.h;
    const dW = newW - d.w, dH = newH - d.h;
    const lcx = (sx * dW) / 2, lcy = (sy * dH) / 2;
    const cx = d.x + d.w / 2 + (lcx * cos - lcy * sin);
    const cy = d.y + d.h / 2 + (lcx * sin + lcy * cos);
    const x = clamp(cx - newW / 2, 0, Math.max(0, bounds.w - newW));
    const y = clamp(cy - newH / 2, 0, Math.max(0, bounds.h - newH));
    const minH = sy ? (newH > d.contentH + 1 ? round(newH) : 0) : o.minH;
    onChange({ ...o, x: round(x), y: round(y), w: round(newW), minH });
  }, [bounds, onChange]);

  const onUp = useCallback(() => {
    drag.current = null;
    document.body.style.cursor = '';
    document.body.classList.remove('wp-demo-dragging');
    suspend(false);
    setInteracting(null);
  }, [suspend]);

  useEffect(() => {
    if (!interacting) return undefined;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); window.removeEventListener('pointercancel', onUp); };
  }, [interacting, onMove, onUp]);
  // never leave the body in dragging state if the object unmounts mid-gesture
  useEffect(() => () => { document.body.classList.remove('wp-demo-dragging'); document.body.style.cursor = ''; }, []);
  const stopMouse = (e) => e.preventDefault(); // mousedown default = start a text selection

  const rotateHint = bind('rotate', { title: 'Rotate', description: 'Drag to spin the sign-in box — hold Shift for 15° steps; it snaps straight near 0°, 90°, 180° and 270°. The form keeps working at any angle.' }, { live: true });
  const resizeHint = bind('resize', flow
    ? { title: 'Resize', description: 'On a desktop you drag these handles to resize the box; the form reflows inside like text in a text box.' }
    : { title: 'Resize', description: 'Drag to resize the sign-in box — the form reflows inside, like text in a text box. The west and north handles move that edge, like the editor.' }, { live: true });

  const geometry = flow
    ? { transform: `rotate(${obj.rot}deg)` }
    : { left: obj.x, top: obj.y, width: obj.w, transform: `rotate(${obj.rot}deg)` };

  return (
    <div
      ref={wrapRef}
      className={`wp-demo-object ${flow ? 'relative w-full' : 'absolute'} ${pulse ? 'wp-demo-pulse' : ''} ${interacting ? 'select-none' : ''}`}
      style={geometry}
      data-pulse={pulse ? 'true' : undefined}
      data-rotation={Math.round(obj.rot)}
      data-width={obj.w}
      data-interacting={interacting || undefined}
      data-testid="landing-auth-object"
    >
      <div className="wp-demo-handles">
        {/* rotation grip */}
        <button
          type="button"
          className="absolute left-1/2 -top-[34px] -translate-x-1/2 flex flex-col items-center z-10 wp-demo-handle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
          style={{ cursor: interacting === 'rotate' ? 'grabbing' : 'grab' }}
          onPointerDown={startRotate}
          onMouseDown={stopMouse}
          aria-label="Rotate the sign-in box"
          {...rotateHint}
          data-testid="demo-rotation-handle"
        >
          <span className="h-[18px] w-[18px] rounded-full bg-white flex items-center justify-center" style={{ border: `2px solid ${SELECTION}` }}><RotateCw className="h-2.5 w-2.5" style={{ color: SELECTION }} /></span>
          <span className="h-[14px] w-px" style={{ background: SELECTION }} />
        </button>
        {interacting === 'rotate' && (
          <span className="absolute left-1/2 -top-[62px] -translate-x-1/2 rounded bg-neutral-900 px-1.5 py-0.5 text-[11px] leading-4 text-white whitespace-nowrap z-20" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="demo-rotation-readout">{Math.round(obj.rot)}°</span>
        )}
        <span className="pointer-events-none absolute inset-0 rounded-xl" style={{ boxShadow: `0 0 0 1px ${SELECTION}` }} aria-hidden="true" />
        {HANDLES.map((pos) => (
          <span
            key={pos}
            role="presentation"
            className="absolute z-10 h-5 w-5 flex items-center justify-center wp-demo-handle"
            style={{ ...HANDLE_AT(pos), cursor: flow ? 'default' : `${pos}-resize` }}
            onPointerDown={(e) => startResize(e, pos)}
            onMouseDown={stopMouse}
            {...resizeHint}
            data-testid={`demo-handle-${pos}`}
          >
            <span className="block h-[9px] w-[9px] rounded-[2px] bg-white" style={{ border: `2px solid ${SELECTION}` }} />
          </span>
        ))}
      </div>
      <div ref={frameRef} className="wp-demo-frame rounded-xl border border-border bg-card text-card-foreground shadow-sm" style={{ minHeight: obj.minH || undefined }} data-testid="demo-auth-frame">
        <div ref={contentRef}>
          <AuthCard frameless mode={mode} onModeChange={onModeChange} initialError={initialError} notice={notice} footer={footer} />
        </div>
      </div>
    </div>
  );
};
